import { useEffect, useMemo } from 'react';

import { useLoyaltyNotificationsRedeemableQuery } from 'generated/graphql-gateway';
import { LoyaltyWidgets, hasComponentKey } from 'pages/loyalty/loyalty-dashboard/types';
import { useFeaturesLoyaltyDashboard } from 'pages/loyalty/loyalty-dashboard/use-feature-loyalty-dashboard';

import { LoyaltyNotifications, NotificationDetails, RedeemableItems } from './types';

const MILLISECONDS_IN_HOUR = 3600000;

export const useLoyaltyNotifications: LoyaltyNotifications = loyaltyUser => {
  const { data, loading: loadingDashboard } = useFeaturesLoyaltyDashboard();
  const widget = data?.widgets?.find(record => {
    if (!record || !hasComponentKey(record)) {
      return false;
    }
    return record.componentKey === LoyaltyWidgets.Notifications;
  });
  const loyaltyId = loyaltyUser?.id;

  // Get all of user's redeemable notification items
  const {
    data: redeemableData,
    loading: loadingUserData,
    refetch,
  } = useLoyaltyNotificationsRedeemableQuery({
    skip: !loyaltyId || !widget,
    variables: {
      rewardsInput: { ignorePointBalance: false },
      offersInput: { global: true },
      loyaltyId: loyaltyId || '',
    },
  });

  // Check if notifications are redeemable on every hour
  useEffect(() => {
    let timeout: number;
    const scheduleNextRefetch = () => {
      const millisecondsTilNextHour =
        MILLISECONDS_IN_HOUR - (new Date().getTime() % MILLISECONDS_IN_HOUR);
      timeout = setTimeout(() => {
        refetchAndSchedule();
      }, millisecondsTilNextHour);
    };
    const refetchAndSchedule = () => {
      scheduleNextRefetch();
      refetch();
    };
    if (loyaltyId) {
      scheduleNextRefetch();
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [refetch, loyaltyId]);

  const redeemableLoyaltyIds = useMemo(() => {
    if (!loyaltyId || !redeemableData?.loyaltyUser) {
      return [];
    }
    const {
      loyaltyUser: { rewards, offers, promotions },
    } = redeemableData;
    let results: string[] = [];
    const addToResults = (records: RedeemableItems) => {
      if (!records) {
        return;
      }
      records.forEach(record => {
        if (record) {
          results.push(record.id);
        }
      });
    };
    addToResults(rewards);
    addToResults(offers);
    addToResults(promotions);
    return results;
  }, [loyaltyId, redeemableData]);

  const redeemableNotifications = useMemo(() => {
    if (!widget || widget.__typename !== 'LoyaltyNotificationsWidget') {
      return [];
    }

    const notifications = widget.notifications || [];
    return notifications.reduce<NotificationDetails[]>((results, notification) => {
      if (!notification?.loyaltyEngineId || !notification.notificationDetails?.enabled) {
        return results;
      }
      const { loyaltyEngineId, notificationDetails: details } = notification;
      if (redeemableLoyaltyIds.indexOf(loyaltyEngineId) >= 0) {
        return results.concat(details);
      }

      return results;
    }, []);
  }, [widget, redeemableLoyaltyIds]);

  return {
    notifications: redeemableNotifications,
    loadingNotifications: loadingDashboard || loadingUserData,
  };
};
